<template>
  <app-page v-if="entityState" full-width>
    <app-state
      :state="entityState"
      :title="stateTitle"
      :message="entityMessage"
      :action-label="entityState === 'error' || entityState === 'offline' ? 'Повторить' : 'К списку документов'"
      @retry="loadShortInfo"
      @action="onStateAction"
    />
  </app-page>

  <document-workbench
    v-else
    v-model="activeSection"
    :document="document"
    :sections="sections"
    :offline="offline"
    :action-reason="actionReason"
  >
    <template #actions>
      <q-btn
        v-if="editRoute"
        color="primary"
        no-caps
        icon="edit"
        label="Редактировать"
        data-testid="document-edit"
        :disable="!editAvailability.allowed || !document.isActive"
        :aria-describedby="!editAvailability.allowed ? 'document-action-reason' : undefined"
        @click="openEdit"
      />
      <q-btn
        v-if="document.type === 'payment'"
        outline
        no-caps
        icon="download"
        label="Выгрузить"
        data-testid="payment-export"
        :disable="offline"
        @click="invokeBody('onDownload')"
      />
      <q-btn-dropdown
        outline
        no-caps
        icon="more_horiz"
        label="Другие действия"
        aria-label="Другие действия с документом"
        data-testid="document-actions"
      >
        <q-list>
          <q-item v-if="primaryPdf" clickable v-close-popup :disable="offline || !canReadFinance" @click="openArtifact(primaryPdf)">
            <q-item-section avatar><q-icon name="open_in_new" aria-hidden="true" /></q-item-section>
            <q-item-section><q-item-label>Открыть PDF с сервера</q-item-label></q-item-section>
          </q-item>
          <q-item v-if="primaryPdf" clickable v-close-popup :disable="offline || !canReadFinance" @click="downloadArtifact(primaryPdf)">
            <q-item-section avatar><q-icon name="download" aria-hidden="true" /></q-item-section>
            <q-item-section><q-item-label>Скачать PDF</q-item-label></q-item-section>
          </q-item>
          <q-item v-for="artifact in xmlArtifacts" :key="artifact.alias" clickable v-close-popup :disable="offline || !canReadFinance" @click="downloadArtifact(artifact)">
            <q-item-section avatar><q-icon name="code" aria-hidden="true" /></q-item-section>
            <q-item-section><q-item-label>Скачать {{ artifact.label }}</q-item-label></q-item-section>
          </q-item>
        </q-list>
      </q-btn-dropdown>
      <q-btn
        v-if="canEditFinance && (document.type !== 'bill_to_pay' || document.body?.source === 'accounting')"
        outline
        no-caps
        :color="document.isActive ? 'negative' : 'primary'"
        :icon="document.isActive ? 'archive' : 'restore'"
        :label="document.isActive ? 'Удалить' : 'Восстановить'"
        data-testid="document-lifecycle-action"
        :disable="!lifecycleAvailability.allowed"
        :aria-describedby="!lifecycleAvailability.allowed ? 'document-action-reason' : undefined"
        @click="invokeBody(document.isActive ? 'onDeleteDocument' : 'onRestoreDocument')"
      />
    </template>

    <template #composition>
      <div class="document-info__body-scroll" data-testid="document-composition">
        <router-view v-slot="{ Component }">
          <component :is="Component" ref="documentBody" :embedded-workbench="true" />
        </router-view>
      </div>
    </template>

    <template #transfers>
      <section aria-labelledby="document-transfers-title">
        <h2 id="document-transfers-title" class="document-info__section-title">Проводки</h2>
        <app-state
          v-if="!document.transfers.length && !editAvailability.allowed"
          state="empty"
          title="Проводок нет"
          message="Для этого документа проводки не сформированы."
        />
        <q-list v-else bordered separator class="document-info__transfers">
          <q-item v-for="item in document.transfers" :key="item.id" class="document-info__transfer">
            <q-item-section>
              <q-item-label>{{ item.name || 'Проводка' }}</q-item-label>
              <q-item-label caption>{{ item.comment || 'Без комментария' }}</q-item-label>
            </q-item-section>
            <q-item-section>
              <q-select
                v-model="item.bill_from"
                :options="billsList"
                label="Счет списания"
                :disable="!editAvailability.allowed"
                @update:model-value="(value) => onTransferBillChange(value && value.value, item.id, 'from')"
              />
            </q-item-section>
            <q-item-section>
              <q-select
                v-model="item.bill_to"
                :options="billsList"
                label="Счет зачисления"
                :disable="!editAvailability.allowed"
                @update:model-value="(value) => onTransferBillChange(value && value.value, item.id, 'to')"
              />
            </q-item-section>
            <q-item-section side>
              <q-toggle
                v-model="item.is_active"
                label="Проведена"
                :disable="!editAvailability.allowed"
                @update:model-value="(value) => onTransferStateChange(value, item.id)"
              />
            </q-item-section>
          </q-item>
        </q-list>

        <q-form v-if="editAvailability.allowed" class="document-info__transfer-form" @submit.prevent="createTransferWildcard">
          <h3>Добавить проводку</h3>
          <div class="document-info__transfer-grid">
            <q-input v-model="wildcard.name" outlined label="Название" :rules="[(value) => !!value || 'Введите название']" />
            <q-input :model-value="wildcardAmountDisplay" outlined inputmode="decimal" label="Сумма" :rules="[() => !!wildcard.amount || 'Введите сумму']" @blur="editingWildcardAmount = false" @update:model-value="editingWildcardAmount = true; wildcard.amount = $event" />
            <q-select v-model="wildcard.billFrom" outlined :options="billsList" label="Счет списания" />
            <q-select v-model="wildcard.billTo" outlined :options="billsList" label="Счет зачисления" />
            <q-input v-model="wildcard.comment" outlined label="Комментарий" />
          </div>
          <q-btn color="primary" no-caps type="submit" label="Добавить проводку" :disable="offline" />
        </q-form>
      </section>
    </template>

    <template #requisites>
      <section aria-labelledby="document-requisites-title">
        <h2 id="document-requisites-title" class="document-info__section-title">Реквизиты</h2>
        <dl class="document-info__requisites">
          <div><dt>ID документа</dt><dd>{{ document.id }}</dd></div>
          <div><dt>Тип</dt><dd>{{ document.typeLabel }}</dd></div>
          <div><dt>Номер</dt><dd>{{ document.number || '—' }}</dd></div>
          <div><dt>Дата</dt><dd>{{ document.date }}</dd></div>
          <div><dt>Контрагент</dt><dd>{{ document.counterparty }}</dd></div>
          <div><dt>Версия</dt><dd>{{ document.isHistorical ? `История №${document.historyId}` : 'Текущая' }}</dd></div>
        </dl>
      </section>
    </template>

    <template #files>
      <section aria-labelledby="document-files-title">
        <h2 id="document-files-title" class="document-info__section-title">Файлы и официальные форматы</h2>
        <server-document-preview
          ref="serverArtifacts"
          :document-type="document.type"
          :document-id="document.id"
          :org-id="connection.org_id"
          :history-id="document.historyId"
          :offline="offline"
          :can-read="canReadFinance"
          @diagnostic="openArtifactDiagnostic"
        />
        <q-list v-if="document.files.length" bordered separator>
          <q-item v-for="file in document.files" :key="file.id" :href="file.url || undefined" :clickable="Boolean(file.url)">
            <q-item-section avatar><q-icon name="attach_file" aria-hidden="true" /></q-item-section>
            <q-item-section>{{ file.name }}</q-item-section>
          </q-item>
        </q-list>
      </section>
    </template>

    <template #history>
      <section aria-labelledby="document-history-title">
        <div class="document-info__history-heading">
          <h2 id="document-history-title" class="document-info__section-title">История</h2>
          <q-btn
            v-if="document.isHistorical"
            flat no-caps icon="history_toggle_off" label="Вернуться к текущей версии"
            :to="historyRoute('last')"
          />
        </div>
        <q-list bordered separator>
          <q-item v-for="item in document.history" :key="item.id" clickable :to="historyRoute(item.id)">
            <q-item-section>
              <q-item-label>{{ item.message }}</q-item-label>
              <q-item-label caption>{{ item.date }} · {{ item.user }}</q-item-label>
            </q-item-section>
            <q-item-section side><q-icon name="chevron_right" aria-hidden="true" /></q-item-section>
          </q-item>
        </q-list>
      </section>
    </template>
  </document-workbench>
  <confirm-dialog
    :model-value="leaveDialogOpen"
    title="Выйти без сохранения?"
    body="Несохраненная проводка будет потеряна."
    confirm-label="Выйти без сохранения"
    cancel-label="Остаться"
    destructive
    @cancel="cancelDirtyLeave"
    @confirm="confirmDirtyLeave"
    @update:model-value="handleLeaveDialogModel"
  />
</template>

<script>
import AppPage from '../../../components/ui/AppPage.vue'
import AppState from '../../../components/ui/AppState.vue'
import ConfirmDialog from '../../../components/ui/ConfirmDialog.vue'
import DocumentWorkbench from '../../../components/documents/DocumentWorkbench.vue'
import ServerDocumentPreview from '../../../components/documents/ServerDocumentPreview.vue'
import { documentArtifactsFor } from '../../../services/backendArtifacts'
import { createDirtyLeaveGuard } from '../../../services/formValidation'
import { formatMoney } from '../../../services/moneyFormat'
import {
  documentActionAvailability,
  documentEditRoute,
  documentTypeFromRoute,
  normalizeWorkbenchDocument
} from '../../../services/documentWorkbench'

function compatibleType(expected, received) {
  if (!expected || !received) return true
  if (expected === received) return true
  return expected === 'factura_avance' && ['avance', 'accept_factura_avance'].includes(received)
}

export default {
  name: 'document_info_root',
  components: { AppPage, AppState, ConfirmDialog, DocumentWorkbench, ServerDocumentPreview },
  beforeRouteLeave: createDirtyLeaveGuard(),
  data() {
    return {
      shortData: {},
      fullData: {},
      activeSection: 'composition',
      entityState: 'loading',
      entityMessage: '',
      actionMessage: '',
      billsList: [],
      wildcard: { name: '', amount: '', comment: '', billFrom: null, billTo: null },
      editingWildcardAmount: false,
      leaveDialogOpen: false,
      pendingNavigation: null,
      handlers: []
    }
  },
  computed: {
    wildcardAmountDisplay() {
      const value = this.wildcard.amount
      if (this.editingWildcardAmount || value === '' || value === undefined || value === null) return value ?? ''
      return formatMoney(value, { useGrouping: false, invalid: String(value) })
    },
    document() {
      return normalizeWorkbenchDocument({
        shortData: this.shortData,
        fullData: this.fullData,
        routeName: this.$route.name,
        historyId: this.$route.params.history_id || 'last'
      })
    },
    canEditFinance() {
      return this.$store.state.permissions.edit_fin_documents === true
    },
    canReadFinance() {
      return this.$store.state.permissions.open_fin_documents === true || this.canEditFinance
    },
    artifactDefinitions() {
      return documentArtifactsFor(this.document.type)
    },
    primaryPdf() {
      return this.artifactDefinitions.find((item) => item.kind === 'pdf') || null
    },
    xmlArtifacts() {
      return this.artifactDefinitions.filter((item) => item.kind === 'xml')
    },
    offline() {
      return this.$store.state.connection_status === 'offline'
    },
    editAvailability() {
      const availability = documentActionAvailability(this.document, { canEdit: this.canEditFinance, offline: this.offline })
      if (availability.allowed && !this.document.isActive) {
        return { allowed: false, reason: 'Удаленный документ нельзя редактировать. Сначала восстановите его.' }
      }
      return availability
    },
    lifecycleAvailability() {
      return documentActionAvailability(this.document, { canEdit: this.canEditFinance, offline: this.offline })
    },
    editRoute() {
      return documentEditRoute(this.document, this.$route.query.context || '')
    },
    actionReason() {
      return this.actionMessage || (!this.editAvailability.allowed ? this.editAvailability.reason : '')
    },
    isDirty() {
      return Object.values(this.wildcard).some((value) => value !== null && value !== '')
    },
    sections() {
      const result = [{ key: 'composition', label: 'Состав' }]
      if (this.document.type !== 'act_sver' && (this.document.transfers.length || this.editAvailability.allowed)) {
        result.push({ key: 'transfers', label: 'Проводки' })
      }
      result.push({ key: 'requisites', label: 'Реквизиты' })
      result.push({ key: 'files', label: 'Файлы' })
      if (this.document.history.length || this.document.isHistorical) result.push({ key: 'history', label: 'История' })
      return result
    },
    stateTitle() {
      if (this.entityState === 'loading') return 'Загружаем документ'
      if (this.entityState === 'not-found') return 'Документ не найден'
      if (this.entityState === 'access-denied') return 'Недостаточно прав'
      if (this.entityState === 'offline') return 'Нет подключения'
      return 'Не удалось загрузить документ'
    }
  },
  watch: {
    '$route.fullPath'() {
      this.activeSection = 'composition'
      this.entityState = 'loading'
      this.shortData = {}
      this.fullData = {}
      this.loadShortInfo()
    },
    sections(value) {
      if (!value.some((section) => section.key === this.activeSection)) this.activeSection = 'composition'
    }
  },
  mounted() {
    this.listen('didReceiveDocumentInfoShort', this.onShortInfo)
    this.listen('didReceiveDocumentInfo', this.onFullInfo)
    this.listen('didReceiveBillListFlatCompact', this.onBills)
    this.listen('onDocumentInfoError', this.setEntityError)
    this.listen('onDocumentInfoShortError', this.setEntityError)
    this.listen('onDeleteDocument', this.onLifecycleResult)
    this.listen('onRestoreDocument', this.onLifecycleResult)
    this.listen('onCreateDocumentTransfer', this.onTransferCreated)
    this.listen('connectionReady', this.loadWorkspace)
    if (this.connection.isAuthenticated) this.loadWorkspace()
  },
  beforeUnmount() {
    this.handlers.forEach(([event, handler]) => this.emitter.off(event, handler))
  },
  methods: {
    listen(event, handler) {
      this.emitter.on(event, handler)
      this.handlers.push([event, handler])
    },
    loadWorkspace() {
      this.loadShortInfo()
      this.connection.gitBillsListFlatCompact(this.connection.makeRequestId('document-workbench-bills'))
    },
    loadShortInfo() {
      this.entityState = 'loading'
      this.entityMessage = ''
      this.connection.getDocumentInfoShort(
        this.connection.makeRequestId('document-workbench-short'),
        this.$route.params.id
      )
    },
    onShortInfo(response) {
      this.shortData = response.data
      const expected = documentTypeFromRoute(this.$route.name)
      if (!compatibleType(expected, response.data.document_type)) {
        this.entityState = 'not-found'
        this.entityMessage = 'Тип документа не соответствует адресу ссылки.'
        return
      }
      this.entityState = null
    },
    onFullInfo(response) {
      const expected = documentTypeFromRoute(this.$route.name)
      if (!compatibleType(expected, response.data.document_type)) {
        this.entityState = 'not-found'
        this.entityMessage = 'Тип документа не соответствует адресу ссылки.'
        return
      }
      this.fullData = response.data
      this.entityState = null
    },
    onBills(response) {
      this.billsList = (Array.isArray(response.bills) ? response.bills : []).map((bill) => ({
        label: bill.full_name,
        value: bill.id
      }))
    },
    setEntityError(error) {
      this.entityState = error.state === 'access-denied' ? 'access-denied' : (error.state || 'error')
      this.entityMessage = error.message || 'Попробуйте повторить запрос или вернуться к списку документов.'
    },
    onStateAction(state) {
      if (!['error', 'offline'].includes(state)) this.$router.push({ name: 'documents_list' })
    },
    openEdit() {
      if (!this.editAvailability.allowed || !this.editRoute) return
      this.$router.push(this.editRoute)
    },
    async invokeBody(method) {
      this.actionMessage = ''
      if (this.offline) {
        this.actionMessage = 'Действие недоступно без подключения к серверу.'
        return
      }
      this.activeSection = 'composition'
      await this.$nextTick()
      await this.$nextTick()
      const body = this.$refs.documentBody
      if (body && typeof body[method] === 'function') body[method]()
      else this.actionMessage = 'Это действие недоступно для выбранного типа документа.'
    },
    openArtifact(artifact) {
      this.activeSection = 'files'
      this.$nextTick(() => this.$refs.serverArtifacts?.openArtifact(artifact))
    },
    downloadArtifact(artifact) {
      this.activeSection = 'files'
      this.$nextTick(() => this.$refs.serverArtifacts?.downloadArtifact(artifact))
    },
    async openArtifactDiagnostic(diagnostic) {
      this.activeSection = 'composition'
      await this.$nextTick()
      await this.$nextTick()
      const body = this.$refs.documentBody
      if (body && typeof body.focusArtifactDiagnostic === 'function') {
        body.focusArtifactDiagnostic(diagnostic)
        return
      }
      const composition = this.$el.querySelector('[data-testid="document-composition"]')
      if (composition) {
        composition.setAttribute('tabindex', '-1')
        composition.focus({ preventScroll: true })
        composition.scrollIntoView({ block: 'start' })
      }
    },
    onLifecycleResult(response) {
      if (response.type === 'error' || response.error) {
        this.actionMessage = response.message || 'Не удалось изменить состояние документа.'
        return
      }
      this.loadShortInfo()
    },
    onTransferCreated(response) {
      if (response.type === 'error' || response.error) {
        this.actionMessage = response.message || 'Не удалось создать проводку.'
        return
      }
      this.wildcard = { name: '', amount: '', comment: '', billFrom: null, billTo: null }
      this.loadShortInfo()
    },
    onTransferStateChange(value, itemId) {
      if (!this.editAvailability.allowed) return
      this.connection.updateTransferStatus(
        this.connection.makeRequestId('document-transfer-state'),
        itemId,
        value ? 'posted' : 'draft'
      )
    },
    onTransferBillChange(value, itemId, direction) {
      if (!this.editAvailability.allowed || !value) return
      this.connection.updateTransferAccounts(
        this.connection.makeRequestId('document-transfer-account'),
        itemId,
        direction === 'from' ? { credit_account_id: value } : { debit_account_id: value }
      )
    },
    createTransferWildcard() {
      if (!this.editAvailability.allowed || !this.wildcard.billFrom || !this.wildcard.billTo) {
        this.actionMessage = 'Заполните название, сумму и оба счета проводки.'
        return
      }
      this.connection.createDocumentTransfer(
        this.connection.makeRequestId('document-transfer-create'),
        {
          document_id: this.$route.params.id,
          amount: this.wildcard.amount,
          name: this.wildcard.name,
          comment: this.wildcard.comment,
          credit_account_id: this.wildcard.billFrom.value,
          debit_account_id: this.wildcard.billTo.value
        }
      )
    },
    requestDirtyLeave(next) {
      this.pendingNavigation = next
      this.leaveDialogOpen = true
    },
    cancelDirtyLeave() {
      const next = this.pendingNavigation
      this.pendingNavigation = null
      this.leaveDialogOpen = false
      if (next) next(false)
    },
    confirmDirtyLeave() {
      const next = this.pendingNavigation
      this.pendingNavigation = null
      this.wildcard = { name: '', amount: '', comment: '', billFrom: null, billTo: null }
      this.leaveDialogOpen = false
      if (next) next()
    },
    handleLeaveDialogModel(value) {
      if (!value && this.leaveDialogOpen) this.cancelDirtyLeave()
    },
    historyRoute(historyId) {
      return {
        name: this.$route.name,
        params: { ...this.$route.params, history_id: String(historyId) },
        query: this.$route.query
      }
    }
  }
}
</script>

<style scoped>
.document-info__body-scroll { width: 100%; max-width: 100%; min-width: 0; }
.document-info__section-title { margin: 0 0 var(--sid-space-4); font-size: var(--sid-font-size-section); }
.document-info__transfers { border-radius: var(--sid-radius-large); }
.document-info__transfer { display: grid; grid-template-columns: minmax(12rem, 1fr) repeat(2, minmax(12rem, 1fr)) auto; gap: var(--sid-space-3); }
.document-info__transfer-form { margin-top: var(--sid-space-5); }
.document-info__transfer-form h3 { margin: 0 0 var(--sid-space-3); }
.document-info__transfer-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--sid-space-3); }
.document-info__requisites { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--sid-space-3); margin: 0; }
.document-info__requisites div { padding: var(--sid-space-4); border: 1px solid var(--sid-color-line); border-radius: var(--sid-radius-large); background: var(--sid-color-surface-0); }
.document-info__requisites dt { color: var(--sid-color-text-secondary); font-size: var(--sid-font-size-label); }
.document-info__requisites dd { margin: var(--sid-space-1) 0 0; overflow-wrap: anywhere; }
.document-info__history-heading { display: flex; align-items: center; justify-content: space-between; gap: var(--sid-space-3); }
@media (max-width: 767px) {
  .document-info__transfer { display: flex; flex-direction: column; align-items: stretch; }
  .document-info__transfer-grid, .document-info__requisites { grid-template-columns: 1fr; }
  .document-info__history-heading { align-items: stretch; flex-direction: column; }
}
</style>
